import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CertifiedBadge } from "@/components/CertifiedBadge";
import { FreshnessGauge } from "@/components/FreshnessGauge";
import WaitlistForm from "@/components/WaitlistForm";
import {
  Building2, Ship, Home, Heart, Radio,
  AlertTriangle, Sparkles, Wind, Smartphone, PackagePlus, PlugZap, BadgeCheck, Search, Eye
} from "lucide-react";

function DeviceRender({ variant = "front" }) {
  // CSS-based product render of the Guest Smell Verify™ hardware device
  if (variant === "side") {
    return (
      <div className="relative w-full aspect-[3/4] flex items-center justify-center">
        <div className="w-14 h-64 rounded-full bg-gradient-to-b from-white via-slate-100 to-slate-200 shadow-2xl border border-slate-200 relative">
          <div className="absolute top-8 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-emerald-500 pulse-ring" />
          <div className="absolute inset-y-16 inset-x-0 border-t border-b border-slate-200/60" />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[8px] tracking-[0.2em] uppercase text-slate-400">Verify™</div>
        </div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 h-3 rounded-full bg-black/10 blur-xl" />
      </div>
    );
  }
  return (
    <div className="relative w-full aspect-square flex items-center justify-center">
      <div className="w-48 h-56 rounded-[36px] bg-gradient-to-b from-white to-slate-100 shadow-2xl border border-slate-200 relative overflow-hidden">
        <div className="absolute top-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 pulse-ring" />
          <span className="text-[10px] tracking-[0.28em] uppercase text-slate-500 font-semibold">Verify™</span>
        </div>
        <div className="absolute inset-x-6 top-14 bottom-16 rounded-2xl bg-[#0A1128] flex flex-col items-center justify-center">
          <div className="font-serif text-4xl text-white">98</div>
          <div className="text-[9px] tracking-[0.28em] uppercase text-[#D4AF37] mt-1">Fresh Score</div>
          <div className="mt-2 flex items-center gap-1">
            {[0,1,2,3].map((i)=>(<div key={i} className="w-1.5 h-1.5 rounded-full bg-emerald-400" />))}
            <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
          </div>
        </div>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[8px] tracking-[0.3em] uppercase text-[#D4AF37]">Guest Smell</div>
      </div>
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-56 h-3 rounded-full bg-black/10 blur-xl" />
    </div>
  );
}

function JourneyStep({ num, icon: Icon, title, desc, dark }) {
  const bg = dark ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200 text-[#0A1128]";
  const sub = dark ? "text-slate-300" : "text-slate-500";
  return (
    <div className={`rounded-2xl p-6 border ${bg}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${dark ? "bg-[#D4AF37] text-[#0A1128]" : "bg-emerald-500 text-white"}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className={`text-[10px] tracking-[0.28em] uppercase ${sub}`}>Step {num}</div>
      </div>
      <h4 className="mt-4 font-serif text-xl">{title}</h4>
      <p className={`mt-2 text-sm ${sub}`}>{desc}</p>
    </div>
  );
}

export default function Vision() {
  return (
    <div>
      {/* HERO / VISION */}
      <section className="bg-[#0A1128] text-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-24">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold">Investor Vision</div>
          <h1 className="mt-3 font-serif text-5xl md:text-6xl tracking-tight leading-[1.05]">
            The trust layer for <br /> <span className="italic text-emerald-400">how a place feels.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            Guest Smell™ starts with vacation rentals. It expands into hotels, senior living, luxury real estate, and cruise lines — anywhere trust in freshness is a purchase driver.
          </p>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-white py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">The Problem</div>
            <h2 className="mt-2 font-serif text-4xl md:text-5xl text-[#0A1128] tracking-tight">Photos can lie. Smells can&apos;t.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              84% of guests report anxiety about how a rental will smell. Reviews mention <em>&quot;musty&quot;</em>, <em>&quot;stale&quot;</em>, or <em>&quot;smoke&quot;</em> more often than <em>&quot;dirty&quot;</em>.
              Hosts have no standard to prove otherwise — and platforms have no way to verify.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { i: AlertTriangle, l: "Guest anxiety", v: "84%" },
              { i: Search,        l: "Odor mentions in reviews", v: "3.2×" },
              { i: Home,          l: "Trust gap in STR",           v: "1 in 4" },
              { i: Wind,          l: "Verification standard today",v: "None" },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl border border-slate-200 p-6 bg-slate-50">
                <s.i className="w-6 h-6 text-emerald-600" />
                <div className="mt-4 font-serif text-3xl text-[#0A1128]">{s.v}</div>
                <div className="text-xs text-slate-500 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">The Solution</div>
            <h2 className="mt-2 font-serif text-4xl md:text-5xl text-[#0A1128] tracking-tight">A freshness standard. Verified live.</h2>
            <p className="mt-4 text-slate-600">A verifiable score, a public certification page, and a badge guests recognize — powered by continuous air, humidity, and cleaning telemetry.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6 items-stretch">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 md:col-span-1">
              <div className="text-[10px] tracking-[0.28em] uppercase text-slate-500 font-semibold">Score</div>
              <div className="mt-4 flex justify-center"><FreshnessGauge score={98} size={200} /></div>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-8 flex flex-col justify-between">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-slate-500 font-semibold">Badge</div>
                <h3 className="mt-3 font-serif text-2xl text-[#0A1128]">The seal of freshness.</h3>
                <p className="mt-2 text-sm text-slate-600">Every certified stay carries a public, verifiable Guest Smell Certified™ badge.</p>
              </div>
              <div className="mt-6 flex justify-center"><CertifiedBadge tier="premium" /></div>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-8">
              <div className="text-[10px] tracking-[0.28em] uppercase text-slate-500 font-semibold">Device</div>
              <h3 className="mt-3 font-serif text-2xl text-[#0A1128]">Verify™ hardware.</h3>
              <div className="mt-3"><DeviceRender /></div>
            </div>
          </div>
        </div>
      </section>

      {/* DEVICE DETAIL */}
      <section id="device" className="bg-white py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14 items-center">
          <div className="rounded-3xl bg-gradient-to-br from-slate-100 to-white p-8 border border-slate-200 grid grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-slate-500 mb-2">Front</div>
              <DeviceRender />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-slate-500 mb-2">Side</div>
              <DeviceRender variant="side" />
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Guest Smell Verify™</div>
            <h2 className="mt-2 font-serif text-4xl md:text-5xl text-[#0A1128] tracking-tight">The freshness sentinel.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              A slim, wall-mounted smart-home device installed near the entryway. White body, soft gold accent, deep navy branding, fresh green status light. Broadcasts freshness telemetry every 60 seconds to the Guest Smell cloud.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                "VOC · CO₂ · PM2.5 · Humidity · Temperature",
                "OTA-updatable firmware, WPA3 Wi-Fi, matter-ready",
                "USB-C powered, magnetic wall mount",
                "Green pulse = certified fresh · Amber = attention needed",
              ].map((f) => (
                <li key={f} className="flex items-center gap-3 text-slate-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Waitlist */}
        <div className="max-w-4xl mx-auto px-6 md:px-10 mt-16" id="waitlist">
          <WaitlistForm />
        </div>
      </section>

      {/* JOURNEYS */}
      <section className="bg-[#0A1128] text-white py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold">Journeys</div>
          <h2 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight">Two paths. One trust.</h2>

          <div className="mt-10">
            <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-400 font-semibold">Host</div>
            <div className="mt-4 grid md:grid-cols-5 gap-4">
              <JourneyStep num={1} icon={PackagePlus} title="Purchase device"  desc="Order Guest Smell Verify™."       dark />
              <JourneyStep num={2} icon={PlugZap}     title="Install near entry" desc="Snap onto magnetic wall mount." dark />
              <JourneyStep num={3} icon={Smartphone}  title="Connect app"      desc="Sign in, pair, and go live."      dark />
              <JourneyStep num={4} icon={Sparkles}    title="Receive score"    desc="Freshness score in minutes."      dark />
              <JourneyStep num={5} icon={BadgeCheck}  title="Display badge"    desc="Publish to Airbnb / Vrbo / site." dark />
            </div>
          </div>

          <div className="mt-14">
            <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-400 font-semibold">Guest</div>
            <div className="mt-4 grid md:grid-cols-4 gap-4">
              <JourneyStep num={1} icon={Search}     title="Search property"    desc="Discover certified stays."   dark />
              <JourneyStep num={2} icon={BadgeCheck} title="See the badge"      desc="Guest Smell Certified™."     dark />
              <JourneyStep num={3} icon={Eye}        title="Review the score"   desc="Air, humidity, cleaning."    dark />
              <JourneyStep num={4} icon={Sparkles}   title="Book with trust"    desc="Deep-link to your platform." dark />
            </div>
          </div>
        </div>
      </section>

      {/* FUTURE */}
      <section className="bg-white py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold">Future Concept</div>
            <h2 className="mt-2 font-serif text-4xl md:text-5xl text-[#0A1128] tracking-tight">Guest Smell Experience™</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              A guest-side companion device that recreates a property&apos;s scent profile <em>before arrival</em>. Cedar loft, sea-air villa, alpine chalet — smell the stay from your living room.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4AF37] bg-[#F9F3E5] text-[#0A1128] text-xs tracking-[0.2em] uppercase font-semibold">
              <Wind className="w-3.5 h-3.5" /> R&amp;D roadmap · 2027
            </div>
          </div>
          <div className="rounded-3xl bg-gradient-to-br from-[#F9F3E5] to-white border border-slate-200 p-10 flex items-center justify-center">
            <div className="relative">
              <div className="w-56 h-56 rounded-full bg-white border-2 border-[#D4AF37] flex items-center justify-center shadow-2xl">
                <Wind className="w-16 h-16 text-emerald-500 scent-drift" />
              </div>
              <div className="absolute -top-3 -right-3 rounded-full bg-[#0A1128] text-[#D4AF37] text-[10px] tracking-[0.28em] uppercase font-semibold px-3 py-1">Concept</div>
            </div>
          </div>
        </div>
      </section>

      {/* MARKETS */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Markets</div>
          <h2 className="mt-2 font-serif text-4xl md:text-5xl text-[#0A1128] tracking-tight">From badge to marketplace.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              { icon: Home,      title: "Short-term rentals", copy: "Airbnb, Vrbo, Booking.com — deep-link today, direct sync tomorrow." },
              { icon: Building2, title: "Hotels & resorts",   copy: "Property- and floor-level certification for global chains." },
              { icon: Heart,     title: "Senior living",      copy: "Continuous air, humidity, and odor safeguards for care facilities." },
              { icon: Ship,      title: "Cruise & travel",    copy: "Cabin, deck, and dining-area freshness assurance." },
              { icon: Radio,     title: "Guest Smell Verify™",copy: "In-room device broadcasting telemetry every 60 seconds." },
              { icon: Sparkles,  title: "Experience Suite",   copy: "Pre-book scent preview and post-stay freshness reports." },
            ].map((v) => (
              <div key={v.title} className="rounded-2xl border border-slate-200 bg-white p-8 hover:-translate-y-1 transition-transform duration-200">
                <v.icon className="w-8 h-8 text-[#D4AF37]" />
                <h3 className="mt-4 font-serif text-2xl text-[#0A1128]">{v.title}</h3>
                <p className="mt-2 text-slate-600 text-sm">{v.copy}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 rounded-3xl bg-[#0A1128] p-10 md:p-14 grid md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold">Ready to invest, host, or partner?</div>
              <h3 className="mt-3 font-serif text-3xl text-white">Let&apos;s build the freshness standard together.</h3>
              <p className="mt-2 text-slate-300 text-sm">Founded by <span className="text-white font-medium">Chalounda Williams</span>. Guest Smell™ — See It. Smell It. Book It.™</p>
            </div>
            <div>
              <Link to="/register"><Button size="lg" className="rounded-full w-full bg-[#D4AF37] hover:bg-[#c19d2e] text-[#0A1128]">Become a Launch Host</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
